import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { Box, Checkbox, Chip, FormControlLabel } from '@mui/material';
import React from 'react';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import { StyledInput } from '../styles/StyledInputs';
import { StyledAvatar, StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';
import { getEntityTypeColor, getEntityTypeLabel } from './entityManagementUtils';

interface EntityManagementDetailsCardProps {
  entity: InvestmentEntity;
  isEditing: boolean;
  editedEntity: { name: string; description: string; isForeignInvestor: boolean };
  onInputChange: (field: 'name' | 'description', value: string) => void;
  onForeignInvestorChange: (value: boolean) => void;
}

export const EntityManagementDetailsCard: React.FC<EntityManagementDetailsCardProps> = ({
  entity,
  isEditing,
  editedEntity,
  onInputChange,
  onForeignInvestorChange,
}) => (
  <StyledCard tone="default" sx={{ mb: 3 }}>
    <StyledCardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <StyledAvatar
          tone="default"
          sx={{
            width: 40,
            height: 40,
            backgroundColor: getEntityTypeColor(entity.type),
            color: 'white',
          }}
        >
          <AccountBalanceIcon />
        </StyledAvatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {isEditing ? (
              <Box sx={{ flex: 1, pr: 2 }}>
                <StyledInput
                  label="Entity Name"
                  value={editedEntity.name}
                  onChange={(e) => onInputChange('name', e.target.value)}
                  required
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <StyledInput
                  label="Description"
                  value={editedEntity.description}
                  onChange={(e) => onInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={editedEntity.isForeignInvestor}
                      onChange={(e) => onForeignInvestorChange(e.target.checked)}
                    />
                  }
                  label="Foreign investor (withholding tax applies to statements)"
                />
              </Box>
            ) : (
              <StyledTypography
                fontWeight={600}
                fontSize="1.125rem"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  pr: 1,
                }}
                title={entity.name || 'Unnamed Entity'}
              >
                {entity.name || 'Unnamed Entity'}
              </StyledTypography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              label={getEntityTypeLabel(entity.type)}
              size="small"
              sx={{
                backgroundColor: getEntityTypeColor(entity.type),
                color: 'white',
              }}
            />
            {entity.isOwner && (
              <Chip label="Owner" size="small" color="success" variant="outlined" />
            )}
            {entity.isForeignInvestor && (
              <Chip label="Foreign Investor" size="small" color="warning" variant="outlined" />
            )}
          </Box>

          {!isEditing && entity.description && (
            <StyledTypography sx={{ mt: 1, lineHeight: 1.4, color: 'text.secondary' }}>
              {entity.description}
            </StyledTypography>
          )}

          <Box sx={{ mt: 1 }}>
            <StyledTypography fontSize="0.875rem" sx={{ color: 'text.secondary' }}>
              Created {new Date(entity.createdAt).toLocaleDateString()}
            </StyledTypography>
          </Box>
        </Box>
      </Box>
    </StyledCardContent>
  </StyledCard>
);
